import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Checkbox,
  FormGroup,
  InputAdornment,
  Divider,
  Paper,
  Chip,
} from '@material-ui/core';
import {
  Person as PersonIcon,
  Business as BusinessIcon,
  AttachMoney as MoneyIcon,
  CheckCircle as CheckIcon,
  ArrowBack as BackIcon,
  ArrowForward as NextIcon,
  Link as LinkIcon,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import InputMask from 'react-input-mask';
import axios from 'axios';
import { BASE_API, USER_TOKEN } from 'config/consts';
import { createPaymentLink } from './paymentLinksActions';
import LoadingProgress from 'common/components/progress/loading';

const RECURRENCE_OPTIONS = [
  { value: 'single', label: 'Pagamento único' },
  { value: 'weekly', label: 'Semanal' },
  { value: 'biweekly', label: 'Quinzenal' },
  { value: 'monthly', label: 'Mensal' },
  { value: 'bimonthly', label: 'Bimestral' },
  { value: 'quarterly', label: 'Trimestral' },
  { value: 'semiannual', label: 'Semestral' },
  { value: 'annual', label: 'Anual' },
];

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: '#f5f5f5',
    minHeight: '100vh',
  },
  container: {
    maxWidth: 900,
    margin: '0 auto',
  },
  pageTitle: {
    fontSize: '1.5rem',
    fontWeight: '500',
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(3),
  },
  card: {
    backgroundColor: 'white',
    borderRadius: theme.spacing(1),
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    padding: theme.spacing(3),
  },
  stepper: {
    backgroundColor: 'transparent',
    paddingBottom: theme.spacing(3),
    '& .MuiStepLabel-label': {
      fontSize: '0.875rem',
    },
  },
  formSection: {
    marginBottom: theme.spacing(3),
  },
  formTitle: {
    marginBottom: theme.spacing(2.5),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    fontSize: '1.125rem',
    fontWeight: '500',
    color: theme.palette.text.primary,
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(4),
  },
  summarySection: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2.5),
    backgroundColor: '#f8f9fa',
    borderRadius: theme.spacing(0.5),
    border: '1px solid #e0e0e0',
  },
  summaryItem: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  summaryLabel: {
    fontWeight: 500,
    color: theme.palette.text.secondary,
  },
  summaryValue: {
    fontWeight: 600,
  },
  percentageContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
  },
  readOnlyField: {
    '& .MuiInputBase-root': {
      backgroundColor: '#f8f9fa',
    },
  },
  successIcon: {
    fontSize: 80,
    color: theme.palette.success.main,
    marginBottom: theme.spacing(2),
  },
  linkUrl: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
    borderRadius: theme.spacing(1),
    wordBreak: 'break-all',
    fontFamily: 'monospace',
    marginTop: theme.spacing(2),
  },
}));

const steps = ['Dados Pessoais', 'Dados do Parceiro', 'Valores', 'Confirmação'];

const PaymentLinkCreate = (props) => {
  const classes = useStyles();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [partners, setPartners] = useState([]);
  const [generatedLink, setGeneratedLink] = useState(null);

  const [formData, setFormData] = useState({
    // Etapa 1 - Dados Pessoais
    activityName: '',
    name: '',
    cpf: '',
    email: '',
    phone: '',

    // Etapa 2 - Dados do Parceiro
    hasPartner: false,
    partnerId: '',
    partnerName: '',
    partnerCnpj: '',
    partnerEmail: '',
    partnerBank: '',
    partnerAccount: '',

    // Etapa 2 - Dados extras do parceiro
    partnerRecipientId: '',
    partnerUserId: '',

    // Etapa 3 - Valores
    value: '',
    partnerPercentage: 0,
    stationPercentage: 100,
    operationFee: 0,
    netValue: 0,
    paymentMethods: {
      boleto: false,
      credit: true,
      debit: true,
      pix: true,
    },
    allowInstallments: false,
    recurrenceType: 'single',
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    // Carregar lista de parceiros
    loadPartners();
  }, []);

  useEffect(() => {
    // Calcular valores automaticamente
    if (formData.value) {
      const value = parseFloat(formData.value);
      const fee = value * 0.05; // 5% de taxa
      const netValue = value - fee;

      setFormData((prev) => ({
        ...prev,
        operationFee: fee,
        netValue: netValue,
      }));
    }
  }, [formData.value, formData.partnerPercentage, formData.stationPercentage]);

  const loadPartners = async () => {
    try {
      const token = JSON.parse(localStorage.getItem(USER_TOKEN));
      const establishments = props.auth?.user?.establishments || [];
      const stationIds = [];
      establishments.forEach((est) => {
        if (est.stations) {
          est.stations.forEach((station) => {
            stationIds.push(station.id);
          });
        }
      });

      if (stationIds.length === 0) return;

      const responses = await Promise.all(
        stationIds.map((stationId) =>
          axios
            .get(`${BASE_API}/gateway-digital-accounts/payment-link-partners/${stationId}`, {
              headers: { Authorization: `${token}` },
            })
            .catch((e) => {
              console.error(`Erro ao buscar parceiros da station ${stationId}:`, e);
              return { data: [] };
            })
        )
      );

      const allPartners = [];
      const partnerKeys = new Set();
      responses.forEach((response) => {
        const data = response.data || [];
        (Array.isArray(data) ? data : []).forEach((partner) => {
          const key = `${partner.user_id}-${partner.establishment_id}`;
          if (partner && partner.user_id && !partnerKeys.has(key)) {
            partnerKeys.add(key);
            const firstAccount = partner.accounts?.[0];
            allPartners.push({
              id: partner.user_id,
              user_id: partner.user_id,
              establishment_id: partner.establishment_id,
              name: partner.user_name || partner.name || '',
              email: partner.user_email || partner.email || '',
              cnpj: partner.cnpj || partner.cpf_cnpj || '',
              establishment_name: partner.establishment_name || '',
              recipient_id: firstAccount?.external_id || '',
              account_id: firstAccount?.local_id || null,
              bank_code: firstAccount?.bank_code || '',
              agency: firstAccount?.agency || '',
              account_number: firstAccount?.account_number
                ? `${firstAccount.account_number}${
                    firstAccount.account_dv ? '-' + firstAccount.account_dv : ''
                  }`
                : '',
              hasDigitalAccount: partner.accounts && partner.accounts.length > 0,
            });
          }
        });
      });

      setPartners(allPartners);
    } catch (error) {
      console.error('Erro ao buscar parceiros:', error);
      setPartners([]);
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 0: // Dados Pessoais
        if (!formData.activityName) newErrors.activityName = 'Campo obrigatório';
        if (!formData.name) newErrors.name = 'Campo obrigatório';
        if (!formData.cpf) newErrors.cpf = 'Campo obrigatório';
        if (formData.cpf && !isValidCPF(formData.cpf)) newErrors.cpf = 'CPF inválido';
        if (formData.email && !isValidEmail(formData.email)) newErrors.email = 'E-mail inválido';
        break;

      case 1: // Dados do Parceiro
        if (formData.hasPartner && !formData.partnerId) {
          newErrors.partnerId = 'Selecione um parceiro';
        }
        if (formData.hasPartner && formData.partnerId && !formData.partnerRecipientId) {
          newErrors.partnerId = 'O parceiro selecionado não possui conta digital (recipient_id)';
        }
        break;

      case 2: {
        // Valores
        if (!formData.value) newErrors.value = 'Campo obrigatório';
        if (parseFloat(formData.value) <= 0) newErrors.value = 'Valor deve ser maior que zero';

        const totalPercentage =
          parseFloat(formData.partnerPercentage) + parseFloat(formData.stationPercentage);
        if (totalPercentage !== 100) {
          newErrors.partnerPercentage = 'A soma dos percentuais deve ser 100%';
          newErrors.stationPercentage = 'A soma dos percentuais deve ser 100%';
        }

        const hasPaymentMethod = Object.values(formData.paymentMethods).some((v) => v);
        if (!hasPaymentMethod) {
          newErrors.paymentMethods = 'Selecione pelo menos uma forma de pagamento';
        }
        break;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidCPF = (cpf) => {
    // Implementar validação de CPF
    return cpf.replace(/\D/g, '').length === 11;
  };

  const isValidEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleInputChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;

    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }

    // Limpar erro do campo
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const handlePartnerToggle = (event) => {
    const hasPartner = event.target.checked;
    setFormData((prev) => ({
      ...prev,
      hasPartner,
      partnerId: '',
      partnerPercentage: hasPartner ? 30 : 0,
      stationPercentage: hasPartner ? 70 : 100,
    }));
  };

  const handlePartnerSelect = (event) => {
    const partnerId = event.target.value;
    const partner = partners.find((p) => p.id === partnerId);

    if (partner) {
      setFormData((prev) => ({
        ...prev,
        partnerId,
        partnerName: partner.name,
        partnerCnpj: partner.cnpj || '',
        partnerEmail: partner.email || '',
        partnerBank: partner.bank_code || '',
        partnerAccount: partner.account_number || '',
        partnerRecipientId: partner.recipient_id || '',
        partnerUserId: partner.user_id || '',
      }));
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(2)) return;

    setLoading(true);
    try {
      const token = JSON.parse(localStorage.getItem(USER_TOKEN));
      const stationId = props.auth.user.establishments[0].stations[0].id;

      // Mapear métodos de pagamento para o formato da API
      const paymentMethodsList = [];
      if (formData.paymentMethods.boleto) paymentMethodsList.push('boleto');
      if (formData.paymentMethods.credit) paymentMethodsList.push('cartao');
      if (formData.paymentMethods.pix) paymentMethodsList.push('pix');

      const payload = {
        customer_name: formData.name,
        customer_cpf: formData.cpf,
        customer_email: formData.email,
        customer_phone: formData.phone,
        service_name: formData.activityName,
        link_value: parseFloat(formData.value),
        payment_methods: paymentMethodsList,
        allow_installments: formData.allowInstallments,
        has_partner: formData.hasPartner,
        station_percentage: parseFloat(formData.stationPercentage),
        partner_percentage: parseFloat(formData.partnerPercentage),
        partner_recipient_id: formData.hasPartner ? formData.partnerRecipientId : null,
        partner_user_id: formData.hasPartner ? formData.partnerUserId : null,
        partner_name: formData.hasPartner ? formData.partnerName : null,
        station_id: stationId,
        recurrence_type: formData.recurrenceType,
      };

      const response = await axios.post(`${BASE_API}/payment-links/generate`, payload, {
        headers: { Authorization: `${token}` },
      });

      if (response.data.success) {
        setGeneratedLink(response.data.payment_link.checkout_url);
        setActiveStep(4);
      } else {
        throw new Error(response.data.message || 'Erro ao gerar link');
      }
    } catch (error) {
      console.error('Error creating payment link:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = () => {
    if (generatedLink) {
      navigator.clipboard.writeText(generatedLink);
      // Mostrar notificação de sucesso
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0: // Dados Pessoais
        return (
          <Box className={classes.formSection}>
            <Typography variant="h6" className={classes.formTitle}>
              <PersonIcon /> Dados Pessoais
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Atividade/Serviço"
                  placeholder="Digite um nome para o link de pagamento"
                  value={formData.activityName}
                  onChange={handleInputChange('activityName')}
                  error={!!errors.activityName}
                  helperText={errors.activityName}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Nome"
                  placeholder="Digite o nome da pessoa para quem o link irá"
                  value={formData.name}
                  onChange={handleInputChange('name')}
                  error={!!errors.name}
                  helperText={errors.name}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <InputMask
                  mask="999.999.999-99"
                  value={formData.cpf}
                  onChange={handleInputChange('cpf')}>
                  {(inputProps) => (
                    <TextField
                      {...inputProps}
                      fullWidth
                      label="CPF"
                      error={!!errors.cpf}
                      helperText={errors.cpf}
                      required
                    />
                  )}
                </InputMask>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="E-mail"
                  type="email"
                  placeholder="Digite o e-mail"
                  value={formData.email}
                  onChange={handleInputChange('email')}
                  error={!!errors.email}
                  helperText={errors.email}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <InputMask
                  mask="(99) 99999-9999"
                  value={formData.phone}
                  onChange={handleInputChange('phone')}>
                  {(inputProps) => <TextField {...inputProps} fullWidth label="Telefone" />}
                </InputMask>
              </Grid>
            </Grid>
          </Box>
        );

      case 1: // Dados do Parceiro
        return (
          <Box className={classes.formSection}>
            <Typography variant="h6" className={classes.formTitle}>
              <BusinessIcon /> Dados do Parceiro
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.hasPartner}
                  onChange={handlePartnerToggle}
                  color="primary"
                />
              }
              label="A atividade é relacionada com parceiro?"
            />

            {formData.hasPartner && (
              <Grid container spacing={3} style={{ marginTop: 16 }}>
                <Grid item xs={12}>
                  <Box
                    style={{
                      backgroundColor: '#e3f2fd',
                      padding: '12px 16px',
                      borderRadius: 4,
                      marginBottom: 8,
                    }}>
                    <Typography variant="body2" style={{ color: '#1565c0' }}>
                      Selecione um parceiro que tenha conta bancária cadastrada para Link de
                      Pagamento.
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth error={!!errors.partnerId}>
                    <InputLabel>Parceiro</InputLabel>
                    <Select value={formData.partnerId} onChange={handlePartnerSelect} required>
                      <MenuItem value="">
                        <em>Selecione um parceiro</em>
                      </MenuItem>
                      {partners.map((partner) => (
                        <MenuItem
                          key={partner.id}
                          value={partner.id}
                          disabled={!partner.hasDigitalAccount}>
                          {partner.name}
                          {partner.establishment_name ? ` - ${partner.establishment_name}` : ''}
                          {!partner.hasDigitalAccount ? ' (sem conta digital)' : ''}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.partnerId && (
                      <Typography variant="caption" color="error">
                        {errors.partnerId}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>
                {formData.partnerId && (
                  <>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Nome"
                        value={formData.partnerName}
                        InputProps={{ readOnly: true }}
                        className={classes.readOnlyField}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="CNPJ"
                        value={formData.partnerCnpj}
                        InputProps={{ readOnly: true }}
                        className={classes.readOnlyField}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="E-mail"
                        value={formData.partnerEmail}
                        InputProps={{ readOnly: true }}
                        className={classes.readOnlyField}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Instituição Financeira"
                        value={formData.partnerBank}
                        InputProps={{ readOnly: true }}
                        className={classes.readOnlyField}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Conta"
                        value={formData.partnerAccount}
                        InputProps={{ readOnly: true }}
                        className={classes.readOnlyField}
                      />
                    </Grid>
                    {formData.partnerRecipientId && (
                      <Grid item xs={12}>
                        <Chip
                          label={`Recipient ID: ${formData.partnerRecipientId}`}
                          color="primary"
                          variant="outlined"
                          size="small"
                        />
                      </Grid>
                    )}
                  </>
                )}
              </Grid>
            )}
          </Box>
        );

      case 2: // Valores
        return (
          <Box className={classes.formSection}>
            <Typography variant="h6" className={classes.formTitle}>
              <MoneyIcon /> Valores
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Valor do link"
                  type="number"
                  value={formData.value}
                  onChange={handleInputChange('value')}
                  error={!!errors.value}
                  helperText={errors.value}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                  }}
                  required
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Tipo de Pagamento</InputLabel>
                  <Select
                    value={formData.recurrenceType}
                    onChange={(e) => {
                      const newType = e.target.value;
                      setFormData((prev) => ({
                        ...prev,
                        recurrenceType: newType,
                        // Se recorrente, forçar apenas cartão de crédito
                        paymentMethods:
                          newType !== 'single'
                            ? { boleto: false, credit: true, debit: false, pix: false }
                            : prev.paymentMethods,
                      }));
                    }}
                    label="Tipo de Pagamento">
                    {RECURRENCE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {formData.recurrenceType !== 'single' && (
                  <Typography
                    variant="caption"
                    style={{ color: '#f57c00', marginTop: 4, display: 'block' }}>
                    Pagamentos recorrentes aceitam apenas cartão de crédito.
                  </Typography>
                )}
              </Grid>

              {formData.hasPartner && (
                <>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Percentual parceiro"
                      type="number"
                      value={formData.partnerPercentage}
                      onChange={handleInputChange('partnerPercentage')}
                      error={!!errors.partnerPercentage}
                      helperText={errors.partnerPercentage}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        inputProps: { min: 0, max: 100 },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Percentual estação"
                      type="number"
                      value={formData.stationPercentage}
                      onChange={handleInputChange('stationPercentage')}
                      error={!!errors.stationPercentage}
                      helperText={errors.stationPercentage}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        inputProps: { min: 0, max: 100 },
                      }}
                    />
                  </Grid>
                </>
              )}

              <Grid item xs={12}>
                <Divider />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Taxa de operação"
                  value={`R$ ${formData.operationFee.toFixed(2).replace('.', ',')}`}
                  InputProps={{ readOnly: true }}
                  className={classes.readOnlyField}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Valor líquido"
                  value={`R$ ${formData.netValue.toFixed(2).replace('.', ',')}`}
                  InputProps={{ readOnly: true }}
                  className={classes.readOnlyField}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Formas de Pagamento
                </Typography>
                {errors.paymentMethods && (
                  <Typography variant="caption" color="error">
                    {errors.paymentMethods}
                  </Typography>
                )}
                <FormGroup row>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.paymentMethods.boleto}
                        onChange={handleInputChange('paymentMethods.boleto')}
                        color="primary"
                        disabled={formData.recurrenceType !== 'single'}
                      />
                    }
                    label="Boleto"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.paymentMethods.credit}
                        onChange={handleInputChange('paymentMethods.credit')}
                        color="primary"
                        disabled={formData.recurrenceType !== 'single'}
                      />
                    }
                    label="Cartão de Crédito"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.paymentMethods.debit}
                        onChange={handleInputChange('paymentMethods.debit')}
                        color="primary"
                        disabled={formData.recurrenceType !== 'single'}
                      />
                    }
                    label="Cartão de Débito"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.paymentMethods.pix}
                        onChange={handleInputChange('paymentMethods.pix')}
                        color="primary"
                        disabled={formData.recurrenceType !== 'single'}
                      />
                    }
                    label="PIX"
                  />
                </FormGroup>
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.allowInstallments}
                      onChange={handleInputChange('allowInstallments')}
                      color="primary"
                    />
                  }
                  label="Permitir parcelamento?"
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 3: // Confirmação
        return (
          <Box className={classes.formSection}>
            <Typography variant="h6" className={classes.formTitle}>
              <CheckIcon /> Confirmação
            </Typography>

            <Paper className={classes.summarySection}>
              <Typography variant="subtitle1" gutterBottom style={{ fontWeight: 'bold' }}>
                Nome da atividade/serviço
              </Typography>
              <Typography>{formData.activityName}</Typography>

              <div className={classes.summaryItem} style={{ marginTop: 8 }}>
                <span className={classes.summaryLabel}>Tipo de pagamento:</span>
                <span className={classes.summaryValue}>
                  {RECURRENCE_OPTIONS.find((o) => o.value === formData.recurrenceType)?.label ||
                    'Pagamento único'}
                </span>
              </div>

              {formData.hasPartner && (
                <>
                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    style={{ fontWeight: 'bold', marginTop: 8 }}>
                    Parceiro
                  </Typography>
                  <Typography>{formData.partnerName || 'Não informado'}</Typography>
                  {formData.partnerRecipientId && (
                    <Typography variant="caption" color="textSecondary">
                      Recipient: {formData.partnerRecipientId}
                    </Typography>
                  )}
                </>
              )}
            </Paper>

            <Paper className={classes.summarySection}>
              <Typography variant="subtitle1" gutterBottom style={{ fontWeight: 'bold' }}>
                Dados do Destinatário
              </Typography>
              <div className={classes.summaryItem}>
                <span className={classes.summaryLabel}>Nome:</span>
                <span className={classes.summaryValue}>{formData.name}</span>
              </div>
              <div className={classes.summaryItem}>
                <span className={classes.summaryLabel}>CPF:</span>
                <span className={classes.summaryValue}>{formData.cpf}</span>
              </div>
              <div className={classes.summaryItem}>
                <span className={classes.summaryLabel}>E-mail:</span>
                <span className={classes.summaryValue}>{formData.email || 'Não informado'}</span>
              </div>
              <div className={classes.summaryItem}>
                <span className={classes.summaryLabel}>Telefone:</span>
                <span className={classes.summaryValue}>{formData.phone || 'Não informado'}</span>
              </div>
            </Paper>

            <Paper className={classes.summarySection}>
              <Typography variant="subtitle1" gutterBottom style={{ fontWeight: 'bold' }}>
                Valores
              </Typography>
              <div className={classes.summaryItem}>
                <span className={classes.summaryLabel}>Valor bruto:</span>
                <span className={classes.summaryValue}>
                  R${' '}
                  {parseFloat(formData.value || 0)
                    .toFixed(2)
                    .replace('.', ',')}
                </span>
              </div>
              <div className={classes.summaryItem}>
                <span className={classes.summaryLabel}>Valor líquido:</span>
                <span className={classes.summaryValue}>
                  R$ {formData.netValue.toFixed(2).replace('.', ',')}
                </span>
              </div>
              {formData.hasPartner && (
                <>
                  <div className={classes.summaryItem}>
                    <span className={classes.summaryLabel}>Percentual estação:</span>
                    <span className={classes.summaryValue}>{formData.stationPercentage}%</span>
                  </div>
                  <div className={classes.summaryItem}>
                    <span className={classes.summaryLabel}>Percentual parceiro:</span>
                    <span className={classes.summaryValue}>{formData.partnerPercentage}%</span>
                  </div>
                </>
              )}
            </Paper>

            <Paper className={classes.summarySection}>
              <Typography variant="subtitle1" gutterBottom style={{ fontWeight: 'bold' }}>
                Forma de pagamento
              </Typography>
              <Box display="flex" gap={1} flexWrap="wrap">
                {formData.paymentMethods.boleto && <Chip label="Boleto" />}
                {formData.paymentMethods.credit && <Chip label="Cartão de Crédito" />}
                {formData.paymentMethods.debit && <Chip label="Cartão de Débito" />}
                {formData.paymentMethods.pix && <Chip label="PIX" />}
              </Box>
              {formData.allowInstallments && (
                <Typography variant="body2" style={{ marginTop: 8 }}>
                  Permite parcelamento: Sim
                </Typography>
              )}
            </Paper>
          </Box>
        );

      case 4: // Sucesso
        return (
          <Box className={classes.formSection} textAlign="center">
            <CheckIcon className={classes.successIcon} />
            <Typography variant="h5" gutterBottom>
              Link de Pagamento Gerado com Sucesso!
            </Typography>
            <Typography variant="body1" color="textSecondary" paragraph>
              O link individual foi gerado e pode ser compartilhado com o destinatário.
            </Typography>
            {generatedLink && (
              <Paper className={classes.linkUrl}>
                <Typography variant="body2" style={{ marginBottom: 8 }}>
                  <LinkIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
                  URL do Link de Pagamento:
                </Typography>
                <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                  {generatedLink}
                </Typography>
              </Paper>
            )}

            <Box mt={3}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleCopyLink}
                style={{ marginRight: 16 }}>
                Copiar Link
              </Button>
              <Button variant="outlined" onClick={() => props.router.push('/owner/payment-links')}>
                Ver Todos os Links
              </Button>
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <LoadingProgress />
      </Box>
    );
  }

  return (
    <div className={classes.root}>
      <div className={classes.container}>
        <Typography className={classes.pageTitle}>Criar Link de Pagamento</Typography>

        <Box className={classes.card}>
          {activeStep < 4 && (
            <Stepper activeStep={activeStep} className={classes.stepper}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          )}
          {renderStepContent()}

          {activeStep < 4 && (
            <Box className={classes.buttonContainer}>
              <Button
                variant="outlined"
                disabled={activeStep === 0}
                onClick={handleBack}
                startIcon={<BackIcon />}>
                Voltar
              </Button>

              {activeStep === steps.length - 1 ? (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  disabled={loading}
                  startIcon={<LinkIcon />}>
                  Gerar Link
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleNext}
                  endIcon={<NextIcon />}>
                  Salvar e continuar
                </Button>
              )}
            </Box>
          )}
        </Box>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      createPaymentLink,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(PaymentLinkCreate);
